(() => {
  'use strict';
  const presentations = new Map();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const allowedNumbers = new Set(['01', '02', '03', '04']);
  const text = value => typeof value === 'string' ? value : '';
  const clamp = value => Math.max(0, Math.min(1, value));
  const ease = value => 1 - Math.pow(1 - clamp(value), 3);
  const projection = {"frameWidth":720,"frameHeight":800,"frames":24,"columns":6,"rows":4,"hingeDegrees":108,"conceptOnly":true,"bagQuads":{"single":[[[0.390396,0.432021],[0.572727,0.490041],[0.403477,0.690546],[0.221146,0.632526]]],"two":[[[0.390396,0.431013],[0.572727,0.489033],[0.403477,0.689538],[0.221146,0.631517]],[[0.608104,0.500291],[0.790435,0.558311],[0.621185,0.758815],[0.438855,0.700795]]],"three":[[[0.390396,0.436055],[0.572727,0.494075],[0.403477,0.69458],[0.221146,0.63656]],[[0.608104,0.497265],[0.790435,0.555285],[0.621185,0.75579],[0.438855,0.69777]],[[0.390396,0.415885],[0.572727,0.473905],[0.403477,0.67441],[0.221146,0.61639]]],"four":[[[0.390396,0.436055],[0.572727,0.494075],[0.403477,0.69458],[0.221146,0.63656]],[[0.608104,0.505333],[0.790435,0.563353],[0.621185,0.763858],[0.438855,0.705838]],[[0.390396,0.415885],[0.572727,0.473905],[0.403477,0.67441],[0.221146,0.61639]],[[0.608104,0.485163],[0.790435,0.543183],[0.621185,0.743688],[0.438855,0.685668]]]},"cardQuad":[[0.176411,0.685299],[0.365952,0.757272],[0.306416,0.840071],[0.118236,0.768532]]};
  const phase = (element, name) => { element.dataset.presentationState = name; };
  const cancel = state => {
    if (state.frame !== null) window.cancelAnimationFrame(state.frame);
    state.frame = null;
  };
  const showFinal = (element, state, name = 'complete') => {
    cancel(state);
    element.classList.remove('vp-is-playing');
    element.classList.add('vp-has-opened');
    state.bags.forEach(bag => { bag.style.opacity = '1'; bag.style.translate = 'none'; });
    state.stationery.style.opacity = '1';
    state.stationery.style.translate = 'none';
    phase(element, name);
  };
  const placePlane = (node, quad, width, height, intrinsicWidth, intrinsicHeight) => {
    const [a, b, , d] = quad;
    const matrix = [(b[0] - a[0]) * width / intrinsicWidth, (b[1] - a[1]) * height / intrinsicWidth,
      (d[0] - a[0]) * width / intrinsicHeight, (d[1] - a[1]) * height / intrinsicHeight,
      a[0] * width, a[1] * height];
    node.style.transform = `matrix(${matrix.join(',')})`;
  };
  const layout = (element, state) => {
    const width = state.scene.clientWidth || state.scene.getBoundingClientRect().width;
    if (!width) return;
    const height = width * projection.frameHeight / projection.frameWidth;
    const quads = projection.bagQuads[['single', 'two', 'three', 'four'][state.count - 1]] || [];
    state.bags.forEach((bag, index) => placePlane(bag, quads[index], width, height, 200, 328));
    placePlane(state.smallCard, projection.cardQuad, width, height, 500, 330);
  };
  const renderFrame = (state, frame) => {
    state.atlas.style.left = `${-(frame % projection.columns) * 100}%`;
    state.atlas.style.top = `${-Math.floor(frame / projection.columns) * 100}%`;
    state.atlas.dataset.frame = String(frame);
  };
  const updateReplay = (element, state) => {
    state.replay.hidden = motion.matches;
    state.replay.disabled = !state.ready || state.failed || !state.count;
  };
  const play = (element, state) => {
    if (state.disposed || !state.ready || state.failed || !state.count) return;
    if (motion.matches) { state.played = true; showFinal(element, state, 'static'); return; }
    cancel(state);
    layout(element, state);
    state.played = true;
    element.classList.remove('vp-has-opened');
    element.classList.add('vp-is-playing');
    state.bags.forEach(bag => { bag.style.opacity = '0'; bag.style.translate = '0 -75px'; });
    state.stationery.style.opacity = '0';
    state.stationery.style.translate = '0 15px';
    renderFrame(state, 0);
    phase(element, 'closed');
    const started = performance.now();
    const stationeryAt = 1850 + state.count * 470 + 250;
    const tick = now => {
      if (state.disposed) return;
      if (motion.matches) { showFinal(element, state, 'static'); return; }
      if (!element.isConnected) { destroy(element); return; }
      const elapsed = now - started;
      if (elapsed >= 300) {
        const hinge = clamp((elapsed - 300) / 1550);
        renderFrame(state, Math.min(projection.frames - 1, Math.floor(hinge * projection.frames)));
        phase(element, hinge < 1 ? 'opening' : 'packing');
      }
      state.bags.forEach((bag, index) => {
        const progress = ease((elapsed - 1850 - index * 470) / 680);
        bag.style.opacity = String(clamp(progress * 2));
        bag.style.translate = `0 ${(-75 * (1 - progress)).toFixed(2)}px`;
      });
      const stationery = ease((elapsed - stationeryAt) / 750);
      state.stationery.style.opacity = String(stationery);
      state.stationery.style.translate = `0 ${(15 * (1 - stationery)).toFixed(2)}px`;
      if (elapsed >= stationeryAt + 750) { showFinal(element, state); return; }
      state.frame = window.requestAnimationFrame(tick);
    };
    state.frame = window.requestAnimationFrame(tick);
  };
  const maybePlay = (element, state) => {
    updateReplay(element, state);
    if (motion.matches) {
      if (state.count) state.played = true;
      showFinal(element, state, 'static');
      return;
    }
    if (!state.ready) return;
    if (motion.matches || state.failed) {
      if (state.count) state.played = true;
      showFinal(element, state, 'static');
    } else if (state.count && state.visible && !state.played) play(element, state);
  };
  const waitForImage = (image, state) => new Promise(resolve => {
    let settled = false;
    const complete = successful => {
      if (settled) return;
      settled = true;
      image.removeEventListener('load', loaded);
      image.removeEventListener('error', failed);
      resolve(successful);
    };
    const loaded = () => {
      if (!image.naturalWidth) { complete(false); return; }
      if (typeof image.decode === 'function') image.decode().then(() => complete(true), () => complete(false));
      else complete(true);
    };
    const failed = () => { image.style.display = 'none'; complete(false); };
    state.mediaCleanup.push(() => complete(false));
    image.addEventListener('load', loaded);
    image.addEventListener('error', failed);
    if (image.complete) loaded();
  });
  const prepareMedia = (element, state) => {
    const epoch = (state.mediaEpoch || 0) + 1;
    state.mediaEpoch = epoch;
    window.clearTimeout(state.mediaTimer);
    state.mediaCleanup.splice(0).forEach(clean => clean());
    state.ready = false;
    state.failed = false;
    updateReplay(element, state);
    const media = [...element.querySelectorAll('[data-presentation-media]')];
    if (!motion.matches) {
      state.atlas.src = state.atlas.dataset.src;
      media.push(state.atlas);
    }
    state.mediaTimer = window.setTimeout(() => {
      if (state.disposed || state.ready || state.mediaEpoch !== epoch) return;
      state.failed = true;
      state.ready = true;
      const poster = element.querySelector('[data-case-poster]');
      element.classList.toggle('vp-case-unavailable', !poster.naturalWidth);
      state.mediaCleanup.splice(0).forEach(clean => clean());
      maybePlay(element, state);
    }, 8000);
    Promise.all(media.map(image => waitForImage(image, state))).then(results => {
      if (state.disposed || state.ready || state.mediaEpoch !== epoch) return;
      window.clearTimeout(state.mediaTimer);
      state.failed = results.some(result => !result);
      state.ready = true;
      const poster = element.querySelector('[data-case-poster]');
      element.classList.toggle('vp-case-unavailable', !poster.naturalWidth);
      if (state.failed) state.bags.forEach(bag => {
        const image = bag.querySelector('img');
        if (image && image.complete && !image.naturalWidth) bag.querySelector('[data-pouch-asset]').classList.add('vp-pouch-image-failed');
      });
      phase(element, 'ready');
      maybePlay(element, state);
    });
  };
  const initialize = element => {
    if (presentations.has(element)) return presentations.get(element);
    const state = { count: 0, signature: '', played: false, visible: false, ready: false, failed: false,
      disposed: false, frame: null, observer: null, resize: null, mediaTimer: null, mediaCleanup: [], bags: [],
      scene: element.querySelector('.vp-scene'), atlas: element.querySelector('[data-case-atlas]'),
      smallCard: element.querySelector('.vp-small-card'), stationery: element.querySelector('[data-visual-stationery]'),
      replay: element.querySelector('[data-replay-opening]') };
    presentations.set(element, state);
    element.classList.toggle('vp-motion-enabled', !motion.matches);
    phase(element, motion.matches ? 'static' : 'loading');
    state.onReplay = () => play(element, state);
    state.replay.addEventListener('click', state.onReplay);
    updateReplay(element, state);
    if ('IntersectionObserver' in window) {
      state.observer = new IntersectionObserver(entries => {
        state.visible = entries.some(entry => entry.isIntersecting);
        maybePlay(element, state);
      }, { threshold: .15 });
      state.observer.observe(state.scene);
    } else state.visible = true;
    state.onResize = () => layout(element, state);
    if ('ResizeObserver' in window) {
      state.resize = new ResizeObserver(state.onResize);
      state.resize.observe(state.scene);
    } else window.addEventListener('resize', state.onResize);
    prepareMedia(element, state);
    return state;
  };
  const makePouch = (url, coffee, index) => {
    const number = String(coffee.number || '').padStart(2, '0');
    const bag = document.createElement('div');
    bag.className = 'vp-case-bag';
    bag.dataset.slot = String(index);
    bag.style.setProperty('--bag-index', index);
    const sprite = document.createElement('div');
    sprite.className = `vp-pouch-sprite vp-pouch-sprite--${allowedNumbers.has(number) ? number : 'unknown'}`;
    sprite.dataset.pouchAsset = '';
    if (allowedNumbers.has(number)) {
      const photo = document.createElement('img');
      photo.src = url;
      photo.alt = '';
      photo.width = 1280;
      photo.height = 1280;
      photo.decoding = 'async';
      photo.addEventListener('error', () => sprite.classList.add('vp-pouch-image-failed'), { once: true });
      sprite.append(photo);
    }
    const fallback = document.createElement('span');
    fallback.className = 'vp-pouch-fallback';
    fallback.textContent = text(coffee.fullTitle) || text(coffee.title);
    sprite.append(fallback);
    bag.append(sprite);
    return bag;
  };
  const update = (element, data = {}) => {
    if (!element || !element.matches('[data-gift-presentation]')) return;
    const state = initialize(element);
    const coffees = Array.isArray(data.coffees) ? data.coffees : [];
    const valid = coffees.length >= 1 && coffees.length <= 4;
    const units = valid ? coffees : [];
    const signature = JSON.stringify(units.map(coffee => [coffee.id, coffee.number, coffee.fullTitle, coffee.title]));
    if (signature !== state.signature) {
      if (state.played) showFinal(element, state, motion.matches || state.failed ? 'static' : 'complete');
      state.bags = units.map((coffee, index) => makePouch(element.dataset.pouchUrl, coffee, index));
      element.querySelector('[data-visual-bags]').replaceChildren(...state.bags);
      element.querySelector('[data-visual-content-list]').replaceChildren(...units.map(coffee => {
        const item = document.createElement('li');
        const label = document.createElement('span');
        label.textContent = text(coffee.fullTitle) || text(coffee.title);
        item.append(label);
        return item;
      }));
      state.signature = signature;
    }
    state.count = units.length;
    element.dataset.bagCount = String(state.count);
    element.querySelector('[data-visual-content-summary]').hidden = !valid;
    element.querySelector('[data-visual-empty]').hidden = valid;
    const card = data.card || {};
    const hasCard = card.mode !== 'none';
    const hasWriting = hasCard && card.mode !== 'blank';
    element.querySelectorAll('[data-card-to]').forEach(node => { node.textContent = hasWriting ? text(card.to) : ''; });
    element.querySelectorAll('[data-card-from]').forEach(node => { node.textContent = hasWriting ? text(card.from) : ''; });
    element.querySelectorAll('[data-card-message]').forEach(node => { node.textContent = hasWriting ? text(card.message) : ''; });
    state.stationery.hidden = !hasCard || !valid;
    element.querySelector('[data-enlarged-card]').hidden = !hasCard || !valid;
    element.querySelector('[data-card-blank]').hidden = !hasCard || hasWriting;
    element.querySelector('[data-no-card]').hidden = hasCard || !valid;
    layout(element, state);
    maybePlay(element, state);
  };
  const destroy = element => {
    const state = presentations.get(element);
    if (!state) return;
    state.disposed = true;
    cancel(state);
    window.clearTimeout(state.mediaTimer);
    state.observer?.disconnect();
    state.resize?.disconnect();
    window.removeEventListener('resize', state.onResize);
    state.replay.removeEventListener('click', state.onReplay);
    state.mediaCleanup.splice(0).forEach(clean => clean());
    presentations.delete(element);
  };
  const within = target => {
    const elements = [...target.querySelectorAll('[data-gift-presentation]')];
    if (target.matches?.('[data-gift-presentation]')) elements.unshift(target);
    return elements;
  };
  document.addEventListener('shopify:section:load', event => within(event.target).forEach(initialize));
  document.addEventListener('shopify:section:unload', event => within(event.target).forEach(destroy));
  window.addEventListener('pagehide', event => {
    if (event.persisted) presentations.forEach((state, element) => showFinal(element, state));
    else [...presentations.keys()].forEach(destroy);
  });
  motion.addEventListener?.('change', () => presentations.forEach((state, element) => {
    state.played = true;
    element.classList.toggle('vp-motion-enabled', !motion.matches);
    showFinal(element, state, 'static');
    if (!motion.matches && !state.atlas.src) prepareMedia(element, state);
    updateReplay(element, state);
  }));
  document.querySelectorAll('[data-gift-presentation]').forEach(initialize);
  window.VousGiftPresentation = Object.freeze({ update });
})();

